import { FLYER_FONTS } from '@pulsify/fonts'
import {
  ASSET_LABEL_MAX_LENGTH,
  FLYER_CREDIT_COST,
  FLYER_DESCRIPTION_MAX_LENGTH,
  FLYER_FORMAT_DIMENSIONS,
  FLYER_TITLE_MAX_LENGTH,
  type FlyerFontChoice,
  type FlyerFormat,
  type FlyerOutput,
  type FlyerQrPlacement,
  flyerImageRatio,
  type GeneratedAsset,
} from '@pulsify/shared/dto'
import { Link } from '@tanstack/react-router'
import { Download, FileText, LayoutTemplate } from 'lucide-react'
import { useCallback, useEffect, useId, useRef, useState } from 'react'
import { toast } from 'sonner'
import { useCurrentBusiness } from '~/entities/business/model/use-business'
import { useGenerateFlyer } from '~/features/ai-studio/hooks'
import { AiProcessingOverlay } from '~/features/shared/components/ai-processing-overlay'
import {
  CreditCostNote,
  CreditCostTag,
  useCreditCost,
} from '~/features/shared/components/credit-cost'
import { ImageUploadField } from '~/features/shared/components/image-upload-field'
import * as m from '~/paraglide/messages'
import { getErrorMessageFromError } from '~/shared/lib/api-errors'
import { urlToFile } from '~/shared/lib/asset-image'
import { cn } from '~/shared/lib/utils'
import { ensureVendoredFontsLoaded } from '~/shared/lib/vendored-fonts'
import { Button } from '~/shared/ui/button'
import { Input } from '~/shared/ui/input'
import { Label } from '~/shared/ui/label'
import { PulsifyLoader } from '~/shared/ui/pulsify-loader'
import { Switch } from '~/shared/ui/switch'
import { Textarea } from '~/shared/ui/textarea'
import { FlyerCropField } from './flyer-crop-field'

const FORMATS: { key: FlyerFormat; label: () => string }[] = [
  { key: 'a4-portrait', label: m.flyerFormatA4Portrait },
  { key: 'a4-landscape', label: m.flyerFormatA4Landscape },
  { key: 'a5-portrait', label: m.flyerFormatA5Portrait },
  { key: 'a6-portrait', label: m.flyerFormatA6Portrait },
  { key: 'ig-story', label: m.flyerFormatIgStory },
]

const PLACEMENTS: { key: FlyerQrPlacement; label: () => string }[] = [
  { key: 'bottom-right', label: m.flyerPlacementBottomRight },
  { key: 'bottom-left', label: m.flyerPlacementBottomLeft },
  { key: 'top-right', label: m.flyerPlacementTopRight },
  { key: 'top-left', label: m.flyerPlacementTopLeft },
  { key: 'center', label: m.flyerPlacementCenter },
]

const OUTPUTS: FlyerOutput[] = ['jpg', 'pdf']

const BACKGROUNDS = ['#ffffff', '#f5f2ec', '#101828', '#1f2937', '#fde68a', '#dbeafe']

interface FlyerGeneratorProps {
  /** Fixes the encoded payload - used when the flyer belongs to an event or place. */
  presetQrData?: string
  /** The entity's own images, cover first; the first one is preloaded as the cover. */
  presetImages?: string[]
  /** Names the entity the flyer belongs to; becomes part of the stored asset title. */
  presetLabel?: string
  presetTitle?: string
  presetDescription?: string
  /** Lets an embedding dialog stay open while a paid generation is in flight. */
  onBusyChange?: (busy: boolean) => void
}

export function FlyerGenerator({
  presetQrData,
  presetImages,
  presetLabel,
  presetTitle,
  presetDescription,
  onBusyChange,
}: FlyerGeneratorProps) {
  const idPrefix = useId()
  const resultRef = useRef<HTMLDivElement>(null)
  const { data: business } = useCurrentBusiness()
  const businessId = business?.id ?? ''
  const generate = useGenerateFlyer(businessId)

  const [file, setFile] = useState<File | null>(null)
  const [preview, setPreview] = useState<string | null>(null)
  const [format, setFormat] = useState<FlyerFormat>('a6-portrait')
  const [freeQrData, setFreeQrData] = useState('')
  const [placement, setPlacement] = useState<FlyerQrPlacement>('bottom-right')
  const [output, setOutput] = useState<FlyerOutput>('jpg')
  const [coverLoading, setCoverLoading] = useState(false)
  const [selectedImage, setSelectedImage] = useState<string | null>(null)
  const [result, setResult] = useState<GeneratedAsset | null>(null)

  const [showImage, setShowImage] = useState(true)
  const [showQr, setShowQr] = useState(true)
  const [title, setTitle] = useState(presetTitle?.slice(0, FLYER_TITLE_MAX_LENGTH) ?? '')
  const [description, setDescription] = useState(
    presetDescription?.slice(0, FLYER_DESCRIPTION_MAX_LENGTH) ?? '',
  )
  const [font, setFont] = useState<FlyerFontChoice>('inter')
  const [background, setBackground] = useState('#ffffff')
  const [focal, setFocal] = useState({ x: 0.5, y: 0.5 })

  const isEmbedded = presetQrData !== undefined
  const qrData = presetQrData ?? freeQrData
  const hasCover = showImage && !!file
  const hasText = !!title.trim() || !!description.trim()
  const { state: creditState } = useCreditCost(businessId, FLYER_CREDIT_COST)
  const canGenerate =
    creditState !== 'short' &&
    (!showQr || qrData.trim().length > 0) &&
    (hasCover || hasText) &&
    !!businessId &&
    !coverLoading &&
    !generate.isPending

  function blockingHint(): string | null {
    if (coverLoading) return null
    if (!hasCover && !hasText) return m.flyerNeedsContent()
    if (showQr && !qrData.trim()) return m.flyerNeedsQrData()
    return null
  }

  const dimensions = FLYER_FORMAT_DIMENSIONS[format]
  const imageRatio = flyerImageRatio({ showImage: hasCover, title, description })
  const cropAspect =
    imageRatio > 0
      ? dimensions.width / (dimensions.height * imageRatio)
      : dimensions.width / dimensions.height

  useEffect(() => {
    ensureVendoredFontsLoaded()
  }, [])

  useEffect(() => {
    return () => {
      if (preview) URL.revokeObjectURL(preview)
    }
  }, [preview])

  useEffect(() => {
    onBusyChange?.(generate.isPending)
  }, [generate.isPending, onBusyChange])

  useEffect(() => {
    if (result) resultRef.current?.scrollIntoView({ block: 'nearest' })
  }, [result])

  // The object URL created here is owned by the [preview] cleanup above.
  const loadFromUrl = useCallback(async (url: string) => {
    setCoverLoading(true)
    try {
      const picked = await urlToFile(url, 'cover.jpg')
      setFile(picked)
      setPreview(URL.createObjectURL(picked))
      setSelectedImage(url)
      setFocal({ x: 0.5, y: 0.5 })
      setResult(null)
    } catch {
      toast.error(m.assetLibraryPickerLoadError())
    } finally {
      setCoverLoading(false)
    }
  }, [])

  useEffect(() => {
    const cover = presetImages?.[0]
    if (cover) loadFromUrl(cover)
  }, [presetImages, loadFromUrl])

  function loadFile(picked: File | null) {
    setFile(picked)
    setPreview(picked ? URL.createObjectURL(picked) : null)
    setSelectedImage(null)
    setFocal({ x: 0.5, y: 0.5 })
    setResult(null)
  }

  async function handleGenerate() {
    setResult(null)
    try {
      const res = await generate.mutateAsync({
        file: showImage ? file : null,
        params: {
          format,
          qrData: qrData.trim(),
          qrPlacement: placement,
          output,
          label: presetLabel?.slice(0, ASSET_LABEL_MAX_LENGTH),
          showImage: hasCover,
          showQr,
          title: title.trim() || undefined,
          description: description.trim() || undefined,
          font,
          background,
          focalX: focal.x,
          focalY: focal.y,
        },
      })
      setResult(res.asset)
      toast.success(m.flyerDone())
    } catch (error) {
      toast.error(getErrorMessageFromError(error))
    }
  }

  return (
    <div className="space-y-5">
      <AiProcessingOverlay open={generate.isPending} />
      <fieldset>
        <legend className="mb-2 text-sm font-medium">{m.flyerFormat()}</legend>
        <div className="flex flex-wrap gap-2">
          {FORMATS.map(({ key, label }) => (
            <Button
              key={key}
              type="button"
              size="sm"
              aria-pressed={format === key}
              variant={format === key ? 'default' : 'outline'}
              onClick={() => setFormat(key)}
            >
              {label()}
            </Button>
          ))}
        </div>
      </fieldset>

      <div className="flex items-center justify-between gap-4 border-t pt-5">
        <Label htmlFor={`${idPrefix}-flyer-show-image`} className="font-semibold">
          {m.flyerSectionImage()}
        </Label>
        <Switch
          id={`${idPrefix}-flyer-show-image`}
          checked={showImage}
          onCheckedChange={setShowImage}
        />
      </div>

      {showImage && (
        <div className="space-y-3">
          <ImageUploadField
            file={file}
            preview={preview}
            onSelect={loadFile}
            compact
            disabled={coverLoading}
          />

          {presetImages && presetImages.length > 0 && (
            <fieldset>
              <legend className="mb-2 text-sm font-medium">{m.flyerEntityImages()}</legend>
              <div className="flex flex-wrap gap-2">
                {presetImages.map((url, index) => (
                  <button
                    key={url}
                    type="button"
                    disabled={coverLoading}
                    aria-label={m.flyerEntityImageNumber({ number: index + 1 })}
                    aria-pressed={selectedImage === url}
                    onClick={() => loadFromUrl(url)}
                    className={cn(
                      'h-16 w-16 overflow-hidden rounded-md border transition-colors disabled:opacity-50',
                      selectedImage === url
                        ? 'border-primary ring-2 ring-primary/40'
                        : 'border-border/60 hover:border-primary/40',
                    )}
                  >
                    <img src={url} alt="" className="h-full w-full object-cover" />
                  </button>
                ))}
              </div>
            </fieldset>
          )}

          {preview && (
            <FlyerCropField
              src={preview}
              aspectRatio={cropAspect}
              focalX={focal.x}
              focalY={focal.y}
              onChange={setFocal}
            />
          )}
        </div>
      )}

      <p className="border-t pt-5 font-semibold text-sm">{m.flyerSectionText()}</p>

      <div className="space-y-2">
        <div className="flex items-center justify-between gap-4">
          <Label htmlFor={`${idPrefix}-flyer-title`}>{m.flyerSectionTitle()}</Label>
          <span className="text-xs text-muted-foreground">
            {title.length}/{FLYER_TITLE_MAX_LENGTH}
          </span>
        </div>
        <Input
          id={`${idPrefix}-flyer-title`}
          value={title}
          maxLength={FLYER_TITLE_MAX_LENGTH}
          placeholder={m.flyerTitlePlaceholder()}
          onChange={(e) => setTitle(e.target.value)}
        />
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between gap-4">
          <Label htmlFor={`${idPrefix}-flyer-description`}>{m.flyerSectionDescription()}</Label>
          <span className="text-xs text-muted-foreground">
            {description.length}/{FLYER_DESCRIPTION_MAX_LENGTH}
          </span>
        </div>
        <Textarea
          id={`${idPrefix}-flyer-description`}
          value={description}
          maxLength={FLYER_DESCRIPTION_MAX_LENGTH}
          rows={3}
          placeholder={m.flyerDescriptionPlaceholder()}
          onChange={(e) => setDescription(e.target.value)}
        />
      </div>

      <fieldset aria-describedby={hasText ? undefined : `${idPrefix}-flyer-font-hint`}>
        <legend className="mb-2 text-sm font-medium">{m.flyerFont()}</legend>
        <div className="flex flex-wrap gap-2">
          {FLYER_FONTS.map((entry) => (
            <Button
              key={entry.id}
              type="button"
              size="sm"
              disabled={!hasText}
              aria-pressed={font === entry.id}
              variant={font === entry.id ? 'default' : 'outline'}
              style={{ fontFamily: `"${entry.family}", sans-serif` }}
              onClick={() => setFont(entry.id)}
            >
              {entry.family}
            </Button>
          ))}
        </div>
        {!hasText && (
          <p id={`${idPrefix}-flyer-font-hint`} className="mt-2 text-xs text-muted-foreground">
            {m.flyerFontNeedsTextHint()}
          </p>
        )}
      </fieldset>

      <fieldset>
        <legend className="mb-2 text-sm font-medium">{m.flyerBackground()}</legend>
        <div className="flex flex-wrap items-center gap-2">
          {BACKGROUNDS.map((color, index) => (
            <button
              key={color}
              type="button"
              aria-label={m.flyerBackgroundPreset({ number: index + 1 })}
              aria-pressed={background === color}
              onClick={() => setBackground(color)}
              style={{ backgroundColor: color }}
              className={cn(
                'h-8 w-8 rounded-md border transition-colors',
                background === color ? 'border-primary ring-2 ring-primary/40' : 'border-border/60',
              )}
            />
          ))}
          <Label htmlFor={`${idPrefix}-flyer-bg`} className="sr-only">
            {m.flyerBackgroundCustom()}
          </Label>
          <Input
            id={`${idPrefix}-flyer-bg`}
            type="color"
            value={background}
            onChange={(e) => setBackground(e.target.value)}
            className="h-9 w-16 p-1"
          />
        </div>
      </fieldset>

      <div className="flex items-center justify-between gap-4 border-t pt-5">
        <Label htmlFor={`${idPrefix}-flyer-show-qr`} className="font-semibold">
          {m.flyerSectionQr()}
        </Label>
        <Switch id={`${idPrefix}-flyer-show-qr`} checked={showQr} onCheckedChange={setShowQr} />
      </div>

      {showQr && (
        <>
          <div className="space-y-2">
            <Label htmlFor={`${idPrefix}-flyer-qr`}>{m.qrData()}</Label>
            <Input
              id={`${idPrefix}-flyer-qr`}
              value={qrData}
              readOnly={isEmbedded}
              className="read-only:cursor-default read-only:bg-muted/40 read-only:text-muted-foreground"
              placeholder={m.qrDataPlaceholder()}
              onChange={(e) => setFreeQrData(e.target.value)}
            />
          </div>

          <fieldset>
            <legend className="mb-2 text-sm font-medium">{m.flyerPlacement()}</legend>
            <div className="flex flex-wrap gap-2">
              {PLACEMENTS.map(({ key, label }) => {
                // Without an image band there is nothing to place a top code on,
                // so those two would fall back to the text band - disable them.
                const needsImage = key === 'top-left' || key === 'top-right'
                return (
                  <Button
                    key={key}
                    type="button"
                    size="sm"
                    disabled={needsImage && imageRatio === 0}
                    aria-pressed={placement === key}
                    variant={placement === key ? 'default' : 'outline'}
                    onClick={() => setPlacement(key)}
                  >
                    {label()}
                  </Button>
                )
              })}
            </div>
          </fieldset>
        </>
      )}

      <fieldset className="border-t pt-5">
        <legend className="mb-2 text-sm font-medium">{m.formatConverterOutput()}</legend>
        <div className="flex gap-2">
          {OUTPUTS.map((value) => (
            <Button
              key={value}
              type="button"
              size="sm"
              aria-pressed={output === value}
              variant={output === value ? 'default' : 'outline'}
              onClick={() => setOutput(value)}
            >
              {value.toUpperCase()}
            </Button>
          ))}
        </div>
      </fieldset>

      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-sm text-muted-foreground">
          <CreditCostNote businessId={businessId} cost={FLYER_CREDIT_COST} />
          {!canGenerate && !generate.isPending && <p className="text-xs">{blockingHint()}</p>}
        </div>
        <Button disabled={!canGenerate} onClick={handleGenerate}>
          {generate.isPending ? (
            <PulsifyLoader className="size-5" tone="on-brand" />
          ) : (
            <LayoutTemplate className="h-4 w-4" />
          )}
          {generate.isPending ? m.flyerGenerating() : m.flyerGenerate()}
          {!generate.isPending && <CreditCostTag cost={FLYER_CREDIT_COST} />}
        </Button>
      </div>

      {result && (
        <div ref={resultRef} className="space-y-3 border-t pt-5">
          <div className="flex items-center justify-between">
            <p className="font-medium">{m.formatConverterResults()}</p>
            <Button asChild variant="link" size="sm">
              <Link to="/dashboard/media">{m.mediaLibrary()}</Link>
            </Button>
          </div>
          {result.mimeType.startsWith('image/') ? (
            <div className="relative inline-block overflow-hidden rounded-lg bg-muted">
              <img src={result.url} alt={m.flyerResultAlt()} className="max-h-80 object-contain" />
              <Button asChild size="icon" variant="secondary" className="absolute right-1 bottom-1">
                <a
                  href={result.url}
                  download
                  target="_blank"
                  rel="noreferrer"
                  aria-label={m.flyerDownload()}
                >
                  <Download className="h-4 w-4" />
                </a>
              </Button>
            </div>
          ) : (
            <Button asChild variant="outline">
              <a href={result.url} download target="_blank" rel="noreferrer">
                <FileText className="h-4 w-4" />
                {m.flyerDownloadPdf()}
              </a>
            </Button>
          )}
        </div>
      )}
    </div>
  )
}
